import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import CloseIcon from 'assets/icons/close'
import PermissionsIcon from 'assets/icons/permissionsOutline'
import { Checkbox, CheckboxGroup } from 'src/components/ConnectedForm'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import { useOrganization, useUpdateInitiative } from 'src/graphql/hooks'

const CustomPrivacyEditor = ({ onCloseButtonClick }) => {
   const { organization } = useOrganization()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()

   const { showNotification } = useContext(NotificationManagerContext)
   const [errorMessage, setErrorMessage] = useState(null)

   const initiativeTypeName = initiative.type.nameSingular

   // Little util to help us
   const getRolePolicy = (roleType, ownerId) => (
      initiative.rolePolicies.find(policy => (
         policy.role.type === roleType && policy.roleOwner.id === ownerId
      ))
   )

   // The various role policies we are concerned with
   const initiativePrimaryRolePolicy = getRolePolicy('primary', initiative.id)
   const communityPrimaryRolePolicy = getRolePolicy('primary', organization.id)
   const publicRolePolicy = getRolePolicy('public', organization.id)

   // The privilege keys themselves
   const spks = policy => policy.privileges.map(p => p.spk)
   const initiativePrimaryRolePolicyPrivilegeSPKs = spks(initiativePrimaryRolePolicy)
   const communityPrimaryRolePolicyPrivilegeSPKs = spks(communityPrimaryRolePolicy)
   const publicRolePolicyPrivilegeSPKs = spks(publicRolePolicy)

   const initialValues = {
      initiativePrimaryRolePolicyPrivilegeSPKs,
      communityPrimaryRolePolicyPrivilegeSPKs,
      publicRolePolicyPrivilegeSPKs
   }

   const handleUpdatedPrivileges = async (values, { setSubmitting }) => {
      try {
         await updateInitiative({ id: initiative.id }, {
            privacy: 'custom',
            rolePolicies: [
               {
                  roleId: initiativePrimaryRolePolicy.role.id,
                  privileges: values.initiativePrimaryRolePolicyPrivilegeSPKs
               },
               {
                  roleId: communityPrimaryRolePolicy.role.id,
                  privileges: values.communityPrimaryRolePolicyPrivilegeSPKs
               },
               {
                  roleId: publicRolePolicy.role.id,
                  privileges: values.publicRolePolicyPrivilegeSPKs
               }
            ]
         })

         setErrorMessage(null)
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         setErrorMessage(err.message)
      } finally {
         setSubmitting(false)
      }
   }

   return (
      <View
         borderRadius="6px[c-f]"
         style={{ overflow: 'hidden' }}
         float="left"
         width="100%"
         height="100%"
         scroll="on">
         <View
            float="left"
            width="100%[a-b] 250px[c-f]"
            height="100%"
            borderRight="1px solid #eee[c-f]"
            padding="18px[a-b] 30px[c-f]">
            <View
               display="flex"
               justifyContent="center"
               alignItems="center"
               width="36px"
               height="36px"
               borderRadius="50%"
               backgroundColor="#000">
               <PermissionsIcon
                  width="20px"
                  height="20px"
                  strokeWidth="2px"
                  color="#fff"
               />
            </View>
            <Spacer size="2" />
            <Text
               size="5"
               color="#000"
               weight="medium">
               Custom Privacy Settings (Advanced)
            </Text>
            <Spacer size="5" />
            <Text size="2" color="#777">
               {`Set specific permissions and privileges for ${initiative.name} as it relates to different roles.`}
            </Text>
         </View>
         <View
            float="left"
            width="100%[a-b] calc(100% - 250px)[c-f]"
            height="100%">
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               float="left"
               width="100%"
               height="60px"
               padding="0px 18px"
               borderBottom="1px solid #eee">
               <View
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  onClick={onCloseButtonClick}>
                  <CloseIcon width="24px" height="24px" />
               </View>
            </View>
            <View
               width="100%"
               height="calc(100% - 60px)[c-f]"
               scroll="on">
               {errorMessage && (
                  <>
                     <Spacer size="3" />
                     <NotificationInline
                        borderRadius="6px"
                        type="error"
                        title="There was an error during save"
                        message={errorMessage}
                     />
                     <Spacer size="3" />
                  </>
               )}
               <View
                  width="100%"
                  padding="0px 20px 20px 20px">
                  <Formik
                     initialValues={initialValues}
                     onSubmit={handleUpdatedPrivileges}
                     render={({ handleSubmit, isSubmitting }) => (
                        <Form onSubmit={handleSubmit}>
                           <View>
                              <View
                                 width="100%"
                                 height="72px"
                                 display="flex"
                                 justifyContent="space-between"
                                 alignItems="center">
                                 <Text
                                    size="2.5"
                                    weight="medium">
                                    Permissions and Privileges by Role
                                 </Text>
                                 <View>
                                    <Button
                                       type="submit"
                                       size="sm"
                                       color="#0dd16d"
                                       name="Save Changes"
                                       mode={isSubmitting ? 'loading' : 'normal'}
                                       rounded
                                    />
                                 </View>
                              </View>
                              <View
                                 display="flex"
                                 borderTop="1px solid #e5e5e5"
                                 padding="18px 0px">
                                 <View flex="0 0 auto" width="40%" paddingRight="60px">
                                    <Text size="2" weight="medium">
                                       {`${initiativeTypeName} ${initiativePrimaryRolePolicy.role.name}`}
                                    </Text>
                                    <Spacer size="1" />
                                    <Text size="1.5" color="#888">
                                       {`Users that are members of the ${initiative.name} ${initiativeTypeName}:`}
                                    </Text>
                                 </View>
                                 <View>
                                    {!initiative.joinable && (
                                       <Text size="1.5" color="#555">
                                          <b>Note: </b>
                                          {`Since joining of this ${initiativeTypeName} is disabled, these privileges below only apply to any users who may have already joined this ${initiativeTypeName}.`}
                                          <br />
                                          <br />
                                       </Text>
                                    )}
                                    <CheckboxGroup
                                       name="initiativePrimaryRolePolicyPrivilegeSPKs">
                                       <Checkbox
                                          highlightColor="#0dd16d"
                                          label={`Can see this ${initiativeTypeName} (including all its content, pages and files) - required`}
                                          value="view"
                                          disabled
                                       />
                                       <Checkbox
                                          highlightColor="#0dd16d"
                                          label={`Can post comments (if enabled) to this ${initiativeTypeName}`}
                                          value="messages.create"
                                       />
                                    </CheckboxGroup>
                                 </View>
                              </View>
                              <View display="flex" borderTop="1px solid #e5e5e5" padding="18px 0px">
                                 <View flex="0 0 auto" width="40%" paddingRight="60px">
                                    <Text size="2" weight="medium">
                                       Community Members
                                    </Text>
                                    <Spacer size="1" />
                                    <Text size="1.5" color="#888">
                                       {`Users that are members of the ${organization.name} Community:`}
                                    </Text>
                                 </View>
                                 <View>
                                    <CheckboxGroup
                                       name="communityPrimaryRolePolicyPrivilegeSPKs">
                                       <Checkbox
                                          highlightColor="#0dd16d"
                                          label={`Can see this ${initiativeTypeName} (including all its content, pages and files)`}
                                          value="view"
                                       />
                                       <Checkbox
                                          highlightColor="#0dd16d"
                                          label={`Can join this ${initiativeTypeName}`}
                                          value="join"
                                       />
                                       <Checkbox
                                          highlightColor="#0dd16d"
                                          label={`Can post comments (if enabled) to this ${initiativeTypeName}`}
                                          value="messages.create"
                                       />
                                    </CheckboxGroup>
                                 </View>
                              </View>
                              <View display="flex" borderTop="1px solid #e5e5e5" padding="18px 0px">
                                 <View flex="0 0 auto" width="40%" paddingRight="60px">
                                    <Text size="2" weight="medium">
                                       Public
                                    </Text>
                                    <Spacer size="1" />
                                    {organization.privacy !== 'secret' && (
                                       <Text size="1.5" color="#888">
                                          Public users can:
                                       </Text>
                                    )}
                                 </View>
                                 <View>
                                    {organization.privacy === 'secret' && (
                                       <Text size="1.5" color="#555">
                                          {`Cannot  see or join this ${initiativeTypeName} since this is a secret community`}
                                       </Text>
                                    )}
                                    {organization.privacy !== 'secret' && (
                                       <CheckboxGroup name="publicRolePolicyPrivilegeSPKs">
                                          <Checkbox
                                             highlightColor="#0dd16d"
                                             label={`Can see this ${initiativeTypeName} (including all its content, pages and files)`}
                                             value="view"
                                          />
                                          <Checkbox
                                             highlightColor="#0dd16d"
                                             label={`Can join this ${initiativeTypeName}`}
                                             value="join"
                                          />
                                       </CheckboxGroup>
                                    )}
                                 </View>
                              </View>
                           </View>
                        </Form>
                     )}
                  />
               </View>
            </View>
         </View>
      </View>
   )
}

CustomPrivacyEditor.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default CustomPrivacyEditor
